<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import axios from "axios";
import { useSearchStore } from "@/js/stores/useSearchStore";
import Icon from "@/js/components/pieces/Icon.vue";
import Pagination from "@/js/components/pieces/Pagination.vue";
import Tips from "@/js/utils/tips";
import { trackAlgoliaSearchClick } from "@/js/utils/algoliaInsights";
import { store } from "@/js/store";

const searchStore = useSearchStore();
const RESULTS_PER_PAGE = 12;
const hasFetchedInitially = ref(false);
const forcedVideoResourceType = "Video";
const idsLoading = ref([]);
const csrftoken = ref({ name: "", value: "" });
const favoriteTip = [
    '<span class="text-xs leading-snug block">',
    '<p><a class="underline text-blue-default" href="/login">Sign in</a> to save your favorites.</p>',
    '<p>Don\'t have an account? <a class="underline text-blue-default" href="/account-creation">It\'s easy and free to sign up</a>!</p>',
    "</span>",
].join("");

const pageFirstResultNumber = computed(() =>
    searchStore.total > 0 ? (searchStore.page - 1) * RESULTS_PER_PAGE + 1 : 0,
);

const pageLastResultNumber = computed(() =>
    Math.min(searchStore.total, searchStore.page * RESULTS_PER_PAGE),
);

const hasVisibleFilters = computed(() =>
    Boolean(
        searchStore.query ||
        searchStore.selectedTopic ||
        searchStore.selectedVideoType,
    ),
);

const isNoResultsState = computed(() =>
    Boolean(
        (searchStore.query ||
            searchStore.selectedTopic ||
            searchStore.selectedResourceType ||
            searchStore.selectedVideoType) &&
            !searchStore.isLoading &&
            searchStore.results.length === 0,
    ),
);

const isEmptyState = computed(() =>
    !searchStore.query &&
    !searchStore.selectedTopic &&
    !searchStore.selectedVideoType &&
    !searchStore.isLoading &&
    searchStore.results.length === 0,
);

const facetTitle = (items, slug) =>
    items.find((item) => String(item.slug) === String(slug))?.title || slug;

const selectedVideoTypeTitle = computed(() =>
    searchStore.selectedVideoType
        ? facetTitle(searchStore.searchVideoTypes, searchStore.selectedVideoType)
        : "",
);

const prevLink = computed(() =>
    searchStore.page > 1
        ? `${window.location.pathname}?page=${searchStore.page - 1}`
        : "",
);

const nextLink = computed(() =>
    searchStore.page < searchStore.totalPages
        ? `${window.location.pathname}?page=${searchStore.page + 1}`
        : "",
);

const syncUrl = () => {
    searchStore.updateURL({
        q: searchStore.query,
        topic: searchStore.selectedTopic,
        resourceType: forcedVideoResourceType,
        videoType: searchStore.selectedVideoType,
        page: searchStore.page,
    });
};

const refreshTips = async () => {
    await nextTick();
    document.querySelectorAll(".video-library-advanced-card-favorite.js-tip").forEach((tip) => {
        if (!tip._tippy) {
            Tips.element(tip);
        }
    });
};

const fetchResults = async () => {
    searchStore.selectedResourceType = forcedVideoResourceType;
    await searchStore.fetchSearchResults();
    await refreshTips();
};

const performSearch = async () => {
    searchStore.page = 1;
    syncUrl();
    await fetchResults();
};

const clearFilter = async (type) => {
    if (["topic", "videoType"].includes(type)) {
        hasFetchedInitially.value = true;
    }

    searchStore.page = 1;

    if (type === "query") searchStore.query = "";
    if (type === "topic") searchStore.selectedTopic = "";
    if (type === "videoType") searchStore.selectedVideoType = "";

    syncUrl();
    await fetchResults();
    hasFetchedInitially.value = false;
};

const paginate = async (targetPage) => {
    if (targetPage >= 1 && targetPage <= searchStore.totalPages) {
        hasFetchedInitially.value = true;
        searchStore.page = targetPage;
        syncUrl();
        await fetchResults();
    }
};

const resultUrl = (result) => {
    const url = result.link || result.url || "#";

    if (!url || url.startsWith("/") || url.startsWith("#")) {
        return url;
    }

    try {
        const parsed = new URL(url);

        if (parsed.hostname.endsWith("readinguniverse.org")) {
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
    } catch (error) {
        return url;
    }

    return url;
};

const resultImage = (result) =>
    result.mainImageUrl ||
    result.imageUrl ||
    result.thumbnail ||
    result.video?.thumbnail ||
    null;

const resultAlt = (result) =>
    result.mainImageAltText || result.imageAltText || result.title || "";

const getPreferredText = (result) => {
    const value = result.teaser || result.autoDescription || result.copy || "";
    return value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
};

const resultId = (result) => {
    const id = Number.parseInt(result.objectID || result.id, 10);

    return Number.isNaN(id) ? null : id;
};

const isResultFavorited = (result) => {
    const id = resultId(result);

    return id ? Object.values(store.favorites).includes(id) : false;
};

const renewCsrfToken = async () => {
    csrftoken.value = await fetch("/actions/blitz/csrf/token")
        .then((result) => result.text())
        .then((result) => ({
            name: "CRAFT_CSRF_TOKEN",
            value: result,
        }));
};

const apiAddToFavorites = async (result) => {
    const id = resultId(result);

    if (!id) return;

    idsLoading.value.push(id);
    await renewCsrfToken();

    let response;

    try {
        response = await axios.post("/actions/readinguniverse/user/favorites-add", {
            [csrftoken.value.name]: csrftoken.value.value,
            id,
        });
    } catch (error) {
        console.error("Error in favorites-add:", error);
    } finally {
        idsLoading.value = idsLoading.value.filter((item) => item !== id);
    }

    if (response?.data) {
        if (Array.isArray(response.data.favorites)) {
            store.updateFavorites(response.data.favorites);
        } else if (response.data.isFavorite !== undefined) {
            if (response.data.isFavorite) {
                if (!Object.values(store.favorites).includes(id)) {
                    store.favorites[id] = id;
                }
            } else {
                delete store.favorites[id];
            }
        }
    }
};

const trackClick = (result, index) => {
    trackAlgoliaSearchClick(
        result,
        (searchStore.page - 1) * RESULTS_PER_PAGE + index + 1,
    );
};

onMounted(async () => {
    const params = new URLSearchParams(window.location.search);
    searchStore.query = params.get("q") || "";
    searchStore.selectedTopic = params.get("topic") || "";
    searchStore.selectedVideoType = params.get("videoType") || "";
    searchStore.selectedResourceType = forcedVideoResourceType;
    searchStore.page = parseInt(params.get("page") || "1");
    hasFetchedInitially.value = true;

    await fetchResults();
    hasFetchedInitially.value = false;
});

watch(
    () => [
        searchStore.selectedTopic,
        searchStore.selectedVideoType,
    ],
    () => {
        if (hasFetchedInitially.value) {
            hasFetchedInitially.value = false;
            return;
        }

        performSearch();
    },
);
</script>

<template>
    <section class="video-library-advanced">
        <div class="video-library-advanced-hero">
            <div class="wrapper video-library-advanced-hero-inner">
                <h1 class="video-library-advanced-title">Video Library</h1>

                <form
                    class="video-library-advanced-form"
                    action="/video-library/advanced-search"
                    method="get"
                    @submit.prevent="performSearch"
                >
                    <!-- Query -->
                    <label for="video-library-advanced-query" class="sr-only">
                        Search video library
                    </label>
                    <input
                        id="video-library-advanced-query"
                        v-model="searchStore.query"
                        class="video-library-advanced-field video-library-advanced-field--query"
                        type="search"
                        name="q"
                        placeholder="Search for..."
                    />

                    <!-- Post-Init Filters -->
                    <template v-if="searchStore.hasFetchedFacets">
                        <!-- Skill -->
                        <label for="video-library-advanced-topic" class="sr-only">
                            Filter by Skill
                        </label>
                        <select
                            id="video-library-advanced-topic"
                            v-model="searchStore.selectedTopic"
                            class="video-library-advanced-field video-library-advanced-select"
                            name="topic"
                        >
                            <option value="">Filter by Skill</option>
                            <option
                                v-for="topic in searchStore.searchTopics"
                                :key="topic.id"
                                :value="topic.title"
                            >
                                {{ topic.title }}
                            </option>
                        </select>

                        <!-- Resource Type -->
                        <input type="hidden" name="resourceType" :value="forcedVideoResourceType" />

                        <!-- Video Type -->
                        <label
                            v-if="searchStore.searchVideoTypes.length"
                            for="video-library-advanced-video-type"
                            class="sr-only"
                        >
                            Filter by Video Type
                        </label>
                        <select
                            v-if="searchStore.searchVideoTypes.length"
                            id="video-library-advanced-video-type"
                            v-model="searchStore.selectedVideoType"
                            class="video-library-advanced-field video-library-advanced-select"
                            name="videoType"
                        >
                            <option value="">Filter by Video Type</option>
                            <option
                                v-for="videoType in searchStore.searchVideoTypes"
                                :key="videoType.id"
                                :value="videoType.slug"
                            >
                                {{ videoType.title }}
                            </option>
                        </select>
                    </template>

                    <!-- Initial Filters -->
                    <template v-else>
                        <!-- Skill -->
                        <select
                            class="video-library-advanced-field video-library-advanced-select"
                            disabled
                            aria-hidden="true"
                            tabindex="-1"
                        >
                            <option>Filter by Skill</option>
                        </select>

                        <!-- Video Type -->
                        <select
                            class="video-library-advanced-field video-library-advanced-select"
                            disabled
                            aria-hidden="true"
                            tabindex="-1"
                        >
                            <option>Filter by Video Type</option>
                        </select>

                    </template>

                    <button
                        class="video-library-advanced-submit"
                        type="submit"
                        aria-label="Search video library"
                    >
                        <Icon name="magnify-glass" class="video-library-advanced-submit-icon" />
                    </button>
                </form>

                <div class="video-library-advanced-summary">
                    <div class="video-library-advanced-tags" v-if="hasVisibleFilters">
                        <span class="video-library-advanced-summary-label">
                            You searched for:
                        </span>

                        <button
                            v-if="searchStore.query"
                            class="video-library-advanced-tag"
                            type="button"
                            @click="clearFilter('query')"
                        >
                            Keyword: "{{ searchStore.query.toLowerCase() }}"
                            <Icon name="close" class="video-library-advanced-tag-icon" />
                        </button>

                        <button
                            v-if="searchStore.selectedTopic"
                            class="video-library-advanced-tag"
                            type="button"
                            @click="clearFilter('topic')"
                        >
                            Topic: "{{ searchStore.selectedTopic }}"
                            <Icon name="close" class="video-library-advanced-tag-icon" />
                        </button>

                        <button
                            v-if="searchStore.selectedVideoType"
                            class="video-library-advanced-tag"
                            type="button"
                            @click="clearFilter('videoType')"
                        >
                            Video Type: "{{ selectedVideoTypeTitle }}"
                            <Icon name="close" class="video-library-advanced-tag-icon" />
                        </button>

                    </div>

                    <p class="video-library-advanced-count" v-if="searchStore.total">
                        Showing
                        <strong>{{ pageFirstResultNumber }}-{{ pageLastResultNumber }} of {{ searchStore.total }}</strong>
                        results
                    </p>
                </div>
            </div>
        </div>

        <div class="wrapper video-library-advanced-results">
            <div class="video-library-advanced-loading" v-if="searchStore.isLoading">
                Loading videos...
            </div>

            <ul class="video-library-advanced-grid" v-else-if="searchStore.results.length">
                <li
                    v-for="(result, index) in searchStore.results"
                    :key="result.objectID"
                    class="video-library-advanced-grid-item"
                >
                    <article class="video-library-advanced-card">
                        <a
                            :href="resultUrl(result)"
                            class="video-library-advanced-card-image-link"
                            :aria-label="`Watch Video: ${result.title}`"
                            @click="trackClick(result, index)"
                        >
                            <figure class="image image--16x9 is-loaded video-library-advanced-card-image">
                                <div class="image-wrapper">
                                    <img
                                        v-if="resultImage(result)"
                                        :src="resultImage(result)"
                                        :alt="resultAlt(result)"
                                        loading="lazy"
                                    />
                                    <span v-else class="video-library-advanced-card-fallback">
                                        <span
                                            v-if="result.displayTitle"
                                            v-html="result.displayTitle"
                                        ></span>
                                        <template v-else>{{ result.title }}</template>
                                    </span>
                                </div>
                            </figure>
                        </a>

                        <div class="video-library-advanced-card-body">
                            <h2 class="video-library-advanced-card-title">
                                <a
                                    :href="resultUrl(result)"
                                    @click="trackClick(result, index)"
                                >
                                    <span
                                        v-if="result.displayTitle"
                                        v-html="result.displayTitle"
                                    ></span>
                                    <template v-else>{{ result.title }}</template>
                                </a>
                            </h2>

                            <p class="video-library-advanced-card-copy" v-if="getPreferredText(result)">
                                {{ getPreferredText(result) }}
                            </p>

                            <div class="video-library-advanced-card-footer">
                                <a
                                    :href="resultUrl(result)"
                                    class="button button--rounded button--blue-default video-library-advanced-card-button"
                                    @click="trackClick(result, index)"
                                >
                                    Watch Video
                                    <span class="video-library-advanced-card-button-icon">
                                        <Icon name="play" />
                                    </span>
                                </a>

                                <button
                                    v-if="store.userId"
                                    class="video-library-advanced-card-favorite"
                                    type="button"
                                    aria-label="Add to favorites"
                                    :disabled="idsLoading.includes(resultId(result))"
                                    @click="apiAddToFavorites(result)"
                                >
                                    <span class="sr-only">
                                        {{ isResultFavorited(result) ? 'In Favorites' : 'Add to Favorites' }}
                                    </span>
                                    <Icon
                                        dir="/icons/icon-selector/"
                                        name="heart"
                                        :class="[
                                            'video-library-advanced-card-heart',
                                            isResultFavorited(result) ? 'is-favorite' : '',
                                            idsLoading.includes(resultId(result)) ? 'is-loading' : ''
                                        ]"
                                    />
                                </button>

                                <button
                                    v-else
                                    class="js-tip video-library-advanced-card-favorite"
                                    type="button"
                                    aria-label="Add to favorites"
                                    :data-tip-content="favoriteTip"
                                    role="tooltip"
                                >
                                    <span class="sr-only">Add to Favorites</span>
                                    <Icon
                                        dir="/icons/icon-selector/"
                                        name="heart"
                                        class="video-library-advanced-card-heart"
                                    />
                                </button>
                            </div>
                        </div>
                    </article>
                </li>
            </ul>

            <div class="video-library-advanced-empty" v-else-if="isNoResultsState">
                <h2>No results found</h2>
                <p>Try adjusting your search or filters.</p>
            </div>

            <div class="video-library-advanced-empty" v-else-if="isEmptyState">
                <h2>Search the video library</h2>
                <p>Enter a keyword or select a reading or writing skill.</p>
            </div>
        </div>

        <Pagination
            v-if="searchStore.totalPages > 1"
            :currentPage="searchStore.page"
            :totalPages="searchStore.totalPages"
            :perPage="RESULTS_PER_PAGE"
            :total="searchStore.total"
            :count="searchStore.results.length"
            :previousLink="prevLink"
            :nextLink="nextLink"
            @paginate="paginate"
        />
    </section>
</template>
